import type { Project } from "@/pages/projects/hooks/useProject";
import { UserRole } from "@/generated/types";
import { Input, DateInput, MoneyInput } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { CreateContractStepOneData } from "../hooks/useCreateContractValidation";
import type { CreateContractValidationErrors } from "../hooks/useCreateContractValidation";

interface CreateContractStepOneProps {
  project: Project;
  data: CreateContractStepOneData;
  errors: CreateContractValidationErrors;
  loading?: boolean;
  onChange: (field: keyof CreateContractStepOneData, value: string | string[]) => void;
  onGenerate: () => void;
  onCancel: () => void;
}

export default function CreateContractStepOne({
  project,
  data,
  errors,
  loading,
  onChange,
  onGenerate,
  onCancel,
}: CreateContractStepOneProps) {
  const eligibleMembers = (project.members ?? []).filter(
    (m) => m.role === UserRole.Client
  );

  const toggleMember = (userId: string) => {
    const next = data.userIds.includes(userId)
      ? data.userIds.filter((id) => id !== userId)
      : [...data.userIds, userId];
    onChange("userIds", next);
  };

  return (
    <div className="space-y-6">
      <Input
        label="Nome do Contrato *"
        value={data.name}
        onChange={(e) => onChange("name", e.target.value)}
        error={errors.name}
        loading={loading}
        required
        variant="primary"
      />

      <MoneyInput
        label="Valor Total *"
        value={data.totalValue}
        onChange={(e) => onChange("totalValue", e.target.value)}
        error={errors.totalValue}
        loading={loading}
        required
        variant="primary"
      />

      <Input
        label="Número de Parcelas *"
        type="number"
        min="1"
        step="1"
        value={data.numberOfInstallments}
        onChange={(e) => onChange("numberOfInstallments", e.target.value)}
        error={errors.numberOfInstallments}
        loading={loading}
        required
        variant="primary"
      />

      <DateInput
        label="Data Primeira Parcela *"
        value={data.startDate}
        onChange={(e) => onChange("startDate", e.target.value)}
        error={errors.startDate}
        loading={loading}
        required
        variant="primary"
      />

      {eligibleMembers.length > 0 && (
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
            Membros do Contrato (opcional)
          </Label>
          <div className="space-y-2">
            {eligibleMembers.map((member) => (
              <label
                key={member.id}
                className="flex items-center gap-3 text-sm text-primary-800 dark:text-primary-200 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={data.userIds.includes(member.id)}
                  onChange={() => toggleMember(member.id)}
                  disabled={loading}
                  className="rounded-none"
                />
                <span>{member.fullName}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          CANCELAR
        </Button>
        <Button type="button" onClick={onGenerate} disabled={loading}>
          GERAR PARCELAS
        </Button>
      </div>
    </div>
  );
}
